<template>
  <div class="collection-tabs">
    <!-- No xs não há largura para abas: vira seletor, o MESMO do ambiente, para os dois
         controles da barra lerem igual. Um v-select aqui herdaria o variant `underlined`
         que é o padrão do projeto, e desenharia um risco solto dentro da barra. -->
    <ontos-shell-menu v-if="$vuetify.display.xs"
                      :label="$tt('collection')" :value="currentLabel">
      <v-list-item v-for="idea of ideas" :key="idea.alias"
                   :to="routeOf(idea)" :title="labelOf(idea)"/>
    </ontos-shell-menu>
    <v-tabs v-else
            :model-value="currentAlias"
            mandatory
            show-arrows
            density="compact">
      <v-tab v-for="idea of ideas" :key="idea.alias" :value="idea.alias" class="collection-tab"
             @click="go(idea)">
        {{ labelOf(idea) }}
        <!-- O atalho de criar nesta coleção: aparece ao passar o mouse ou ao chegar pelo teclado, e
             leva direto à página de criar, como o "+ Criar" da barra — na barra do app,
             criar é rota. O caminho que se vê é o "+" da barra; este é para quem já sabe qual quer.

             `.stop` porque o clique subiria para a aba, que navegaria até a LISTA da coleção. Um
             `span`, e não um `v-btn`: a aba já é um botão, e botão dentro de botão é marcação
             inválida — por isso o `role` e o `tabindex` à mão, o mesmo arranjo do atalho de mover no
             card do Kanban. -->
        <span v-if="creatable(idea)" class="collection-tab-create" role="button" tabindex="0"
              :title="$tt('instance-create', {collection: idea.name})"
              @click.stop="create(idea)"
              @keydown.enter.stop.prevent="create(idea)">
          <v-icon size="14">mdi-plus</v-icon>
        </span>
      </v-tab>
    </v-tabs>
  </div>
</template>

<script lang="ts">
import {Component, Vue, Prop, toNative} from 'vue-facing-decorator'
import Idea from '@/types/Idea'
import {EnhancedWorkpace} from '@/types/Workspace'
import {collectionRoute} from '@/utils/breadcrumb'
import ShellMenu from '@/components/shell/ShellMenu.vue'
import {canCreateIn, createRoute} from '@/utils/instanceCreation'

/**
 * As coleções do ambiente, lado a lado na barra. Substitui a navegação lateral.
 *
 * **A seleção é dirigida pelo modelo, não pelo link**, e isso não é preferência: as
 * rotas do app são IRMÃS, não aninhadas — `list-instances` e `view-instance` são
 * registros separados —, então o `:to` de uma aba não fica ativo dentro de um item. Como
 * o VTabs nasce com `mandatory: 'force'` e o grupo seleciona o primeiro item não
 * desabilitado quando nada está selecionado, a aba da PRIMEIRA coleção acenderia em toda
 * rota mais funda, mentindo sobre onde se está.
 *
 * Casando `:value` com `$route.params.idea` a aba certa acende em todas as rotas fundas
 * de uma vez — dentro do item, no Kanban, no desenho da coleção — e `:mandatory="false"`
 * faz nada acender onde não há coleção nenhuma, como na home do ambiente e nas telas de
 * fluxo.
 *
 * O `mandatory` é `true`, e não `'force'` nem `false` — os três se comportam diferente
 * (composables/group.js). Com `'force'` o grupo acenderia a primeira coleção sozinha
 * onde nada devesse acender. Com `false`, clicar na aba JÁ ACESA a desselecionava, e o
 * `model-value` a reacendia no quadro seguinte: a aba piscava. Com `true`, a linha 163
 * do group.js ignora o clique na já selecionada, e o `forceMandatoryValue` não roda
 * porque ele exige `'force'`. Desselecionar pelo MODELO continua valendo, que é o que
 * apaga tudo fora de uma coleção.
 *
 * **E por isso a navegação é por `@click`, e não por `:to`.** Com `:to`, o Vuetify liga o
 * `useSelectLink` (composables/selectLink.js), que observa se o LINK está ativo e manda o
 * grupo selecionar ou DESSELECIONAR conforme — passando por cima do `model-value`. Como
 * cada aba aponta para um destino só, a aba da coleção acendia na lista e apagava no
 * Kanban, ou o contrário. E o deslizador vem do grupo (VTab.js:46), então nem forçar
 * `:active` no botão o traria de volta.
 *
 * Uma rota própria de coleção que redirecionasse não resolve: o `isActive` compara os
 * registros de rota casados, e o registro que redireciona não fica no `matched` depois do
 * redirecionamento — o link continuaria inativo. O que resolveria com link de verdade
 * seriam rotas ANINHADAS, coleção de pai e lista/Kanban de filhas, porque aí o link para
 * o pai fica ativo em qualquer filha. É mudança grande, e fica anotada como opção.
 *
 * O que se perde sem `:to` é o href: ctrl+clique e "abrir em outra aba" deixam de
 * funcionar NESTAS abas. O caminho e a barra de endereço continuam dando a URL.
 *
 * O destino de cada coleção vem do utils/breadcrumb, compartilhado com o caminho — se
 * cada um calculasse o seu, clicar na aba e clicar no degrau levariam a telas diferentes.
 */
@Component({name: 'ontos-collection-tabs', components: {'ontos-shell-menu': ShellMenu}})
class CollectionTabs extends Vue {
  @Prop({required: true}) readonly workspace!: EnhancedWorkpace

  get ideas(): Idea[] {
    return this.workspace && this.workspace.ideas || []
  }

  creatable(idea: Idea): boolean {
    return canCreateIn(this.workspace, idea)
  }

  create(idea: Idea) {
    this.$router.push(createRoute(this.workspace.alias, idea.alias)).catch(() => undefined)
  }

  get currentAlias(): string | null {
    return (this.$route.params.idea as string) || null
  }

  labelOf(idea: Idea): string {
    return idea.pluralName
  }

  /**
   * A lista ou o Kanban da coleção, de qualquer tela — também de dentro do design dela (pedido do
   * usuário). Já levou ao desenho da coleção quando a casca tinha um modo de design.
   */
  routeOf(idea: Idea) {
    return collectionRoute(idea, this.$route.params.system as string)
  }

  go(idea: Idea) {
    //Clicar na aba da coleção em que já se está, estando numa VISÃO dela, não faz nada:
    //a rota da coleção mandaria de volta para a mesma tela, e o caminho de ida e volta
    //desmonta e remonta a página à toa. Estando mais fundo — dentro de um item, numa
    //propriedade, no design dela — o clique vale, e é o jeito de ir à coleção.
    const routes = ['list-instances', 'kanban']
    if (idea.alias === this.currentAlias && routes.includes(this.$route.name as string)) return
    //Navegar para a rota em que já se está é rejeição esperada do vue-router, não erro
    this.$router.push(this.routeOf(idea)).catch(() => undefined)
  }

  /**
   * Vazio fora de uma coleção: aí o seletor mostra só a palavra "Coleção", que sozinha
   * já é o convite a escolher uma. Ver o ShellMenu.
   */
  get currentLabel(): string {
    const idea = this.currentAlias && this.workspace.ideaWith(this.currentAlias)
    return idea ? this.labelOf(idea) : ''
  }
}

export default toNative(CollectionTabs)
</script>

<style scoped>
/* `min-width: 0` não é enfeite: sem ele o item flex não encolhe abaixo do conteúdo, as
   setas de transbordo nunca entram, e como o conteúdo da barra é `overflow: hidden` o
   que sobra à direita — o avatar — é CORTADO em vez de as abas rolarem. */
.collection-tabs {
  flex: 1 1 auto;
  min-width: 0;
}

/* O "+" fica POR CIMA da folga da direita da aba, sem ocupar lugar no layout, e só acende no hover.
   No fluxo ele empurraria as abas vizinhas a cada passada do mouse. Com o lugar reservado, cada aba
   ficava mais larga só do lado direito, e o nome descentralizado — mesmo sem mouse nenhum (relato do
   usuário). A folga já existe: é o padding do botão, e o "+" cabe nela.

   Apagado, ele não recebe clique — o clique ali é da aba. */
.collection-tab-create {
  position: absolute;
  top: 50%;
  right: 1px;
  display: inline-flex;
  padding: 1px;
  border-radius: 50%;
  opacity: 0;
  pointer-events: none;
  transform: translateY(-50%);
  transition: opacity 0.15s ease, background-color 0.15s ease;
}

/* `:focus-visible`, e não `:focus-within`: clicar na aba a deixa com o foco, e o "+" ficava aceso
   depois de o mouse sair (relato do usuário). O `:focus-visible` só vale quando o foco veio do
   teclado — que é para quem esta regra existe. */
.collection-tab:hover .collection-tab-create,
.collection-tab:focus-visible .collection-tab-create,
.collection-tab-create:focus-visible {
  opacity: 0.85;
  pointer-events: auto;
}

.collection-tab-create:hover {
  opacity: 1;
  background-color: rgba(255, 255, 255, 0.15);
}

</style>
